import type { ReactNode } from 'react';
import { useMenu, type MenuItemActions } from '../../../shared/hooks/useMenu.js';
import { useAuth } from '../auth/AuthProvider.js';

export type CanAction = keyof MenuItemActions;

/**
 * Whether the caller may take an action on a page — the single decision both
 * {@link Can} and any screen that needs the answer as a value read from.
 *
 * Kept as one function so a control that hides itself and a field that goes
 * read-only can never disagree about the same grant.
 */
export function useCan(path: string, action: CanAction): boolean {
  const { token } = useAuth();
  // 'web' is declared here for the same reason the Sidebar declares it: the
  // endpoint serves both clients, and a caller that stays silent is handed the
  // mobile-only rows too. Left silent, this hook answered from a *second*,
  // unfiltered menu — `loadMenu` caches on apiBase|channel|token — so the
  // sidebar and the guards held two different answers to the same question.
  const { menu, loading, error } = useMenu('/api', token, 'web');

  // Still loading: treat as "not yet", so no control flashes before the answer
  // arrives. An error, or a backend that sends no action grants at all, falls
  // open — the server guard is the real boundary.
  if (loading) return false;
  if (error) return true;

  const item = menu?.items.find(
    (it) => it.path === path || path.startsWith(`${it.path}/`),
  );
  if (!item || !item.actions) return true;
  return !!item.actions[action];
}

/**
 * Can — action-level RBAC gate for buttons, links and fields (X-RBAC-1).
 *
 *   <Can path="/admin/users" action="delete">
 *     <button onClick={remove}>Delete</button>
 *   </Can>
 *
 * The per-page action grants (view/create/edit/delete/approve/reject/export/
 * print) are decided by the administrator in Role Manager and delivered on each
 * menu item via `GET /api/me/menu` ({@link useMenu}). Nothing is hardcoded — the
 * button is shown iff the caller's roles were granted that action on that page.
 *
 * This is a UX/defence-in-depth layer only: the backend @RequirePermission
 * guard is the real enforcement boundary. Accordingly the gate fails OPEN when
 * the signal is unavailable (menu still loading is treated as "hide" to avoid a
 * flash of an unauthorized control; a menu-fetch error, or a backend that does
 * not send `actions`, is treated as "allow" so the server stays authoritative).
 */
export function Can({
  path,
  action,
  children,
  fallback = null,
}: {
  /** Canonical menu path whose action grants gate this control. */
  readonly path: string;
  readonly action: CanAction;
  readonly children: ReactNode;
  /** Rendered when the action is not permitted. Defaults to nothing. */
  readonly fallback?: ReactNode;
}): ReactNode {
  return useCan(path, action) ? <>{children}</> : <>{fallback}</>;
}
